import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Eye, Search } from 'lucide-react';
import { useMatchHistoryList } from '@/hooks/useTeacherMatchHistory';
import { MatchHistoryDetailDialog } from './MatchHistoryDetailDialog';

function fmt(iso: string): string {
  try {
    return new Intl.DateTimeFormat('th-TH', {
      timeZone: 'Asia/Bangkok',
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).format(new Date(iso));
  } catch { return iso; }
}

export const MatchHistoryList: React.FC = () => {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [openId, setOpenId] = useState<string | null>(null);
  const { data, isLoading, error } = useMatchHistoryList({ search, status });

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="ค้นหานักเรียน / รหัส / อีเมลผู้สร้าง"
            className="pl-8"
          />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-full sm:w-[180px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">ทุกสถานะ</SelectItem>
            <SelectItem value="pending">รอตอบ</SelectItem>
            <SelectItem value="complete">ปิดแล้ว</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading && (
        <div className="space-y-2">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-14 w-full" />)}
        </div>
      )}
      {error && <div className="text-destructive text-sm">{(error as Error).message}</div>}

      {!isLoading && !error && (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>วันที่สร้าง</TableHead>
                <TableHead>นักเรียน</TableHead>
                <TableHead>ผู้สร้าง</TableHead>
                <TableHead>หมดอายุ</TableHead>
                <TableHead className="text-center">ส่ง / ตอบรับ / ปฏิเสธ</TableHead>
                <TableHead>สถานะ</TableHead>
                <TableHead className="w-[80px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data || []).length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">ไม่พบประวัติ</TableCell></TableRow>
              )}
              {(data || []).map(r => (
                <TableRow key={r.id}>
                  <TableCell className="text-sm whitespace-nowrap">{fmt(r.created_at)}</TableCell>
                  <TableCell className="text-sm">{r.student_label}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.created_by_email || '-'}</TableCell>
                  <TableCell className="text-sm whitespace-nowrap">{fmt(r.expire_at)}</TableCell>
                  <TableCell className="text-center text-sm whitespace-nowrap">
                    <span>{r.counts.sent}</span>
                    <span className="mx-1 text-muted-foreground">/</span>
                    <span className="text-green-700 font-medium">{r.counts.confirmed}</span>
                    <span className="mx-1 text-muted-foreground">/</span>
                    <span className="text-destructive font-medium">{r.counts.denied}</span>
                    <span className="text-muted-foreground"> (จาก {r.counts.total})</span>
                  </TableCell>
                  <TableCell>
                    {r.status === 'pending'
                      ? <Badge variant="secondary">รอตอบ</Badge>
                      : <Badge variant="outline">{r.status}</Badge>}
                  </TableCell>
                  <TableCell>
                    <Button size="sm" variant="ghost" onClick={() => setOpenId(r.id)}>
                      <Eye className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <MatchHistoryDetailDialog requestId={openId} onOpenChange={(o) => !o && setOpenId(null)} />
    </div>
  );
};
